/**
 * Data Sync Utilities
 * Handles synchronization between Firestore and localStorage
 * Bi-directional sync:
 * - Downloads user data from Firestore when localStorage is empty (new browser/domain)
 * - Detects when admin has deleted user data and clears localStorage accordingly
 */

import { aggregationService } from '../services/AggregationService';
import { firestoreService } from '../services/database/FirestoreService';
import { resetPointsSessionState, syncPointsFromFirestore } from '../services/PointsManager';
import { syncDailyActivitiesFromFirestore } from './globalStreakUtils';
import { clearReconcileMarkers } from './streakWithFreezes';

/**
 * Clear user-specific data from localStorage
 */
export function clearUserDataFromLocalStorage(
  userId: string,
  options: {
    clearProgress?: boolean;
    clearKeyboards?: boolean;
    clearProfile?: boolean;
  }
): void {
  console.log('🧹 Clearing localStorage for user:', userId, options);

  if (options.clearProgress) {
    // Clear level completions
    const levelCompletionKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`level_completion_${userId}_`)) {
        levelCompletionKeys.push(key);
      }
    }
    levelCompletionKeys.forEach(key => localStorage.removeItem(key));
    console.log(`  ✅ Cleared ${levelCompletionKeys.length} level completions`);

    // Clear aggregations
    const aggregationKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`aggregation_${userId}_`)) {
        aggregationKeys.push(key);
      }
    }
    aggregationKeys.forEach(key => localStorage.removeItem(key));
    console.log(`  ✅ Cleared ${aggregationKeys.length} aggregations`);

    // Clear training sessions (old format)
    const sessionKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith(`basicsProgress_${userId}`) || 
        key.startsWith(`pythonProgress_${userId}`) ||
        key.startsWith(`Progress_${userId}`)
      )) {
        sessionKeys.push(key);
      }
    }
    sessionKeys.forEach(key => localStorage.removeItem(key));
    console.log(`  ✅ Cleared ${sessionKeys.length} training sessions`);
  }

  if (options.clearKeyboards) {
    // Clear keyboards
    const keyboardKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`keyboard_${userId}_`)) {
        keyboardKeys.push(key);
      }
    }
    keyboardKeys.forEach(key => localStorage.removeItem(key));
    console.log(`  ✅ Cleared ${keyboardKeys.length} keyboards`);

    // Clear default keyboard
    const defaultKeyKey = `user_default_keyboard_${userId}`;
    localStorage.removeItem(defaultKeyKey);
  }

  if (options.clearProfile) {
    // Clear user profile cache
    const profileKey = `userProfile_${userId}`;
    localStorage.removeItem(profileKey);
    console.log(`  ✅ Cleared user profile`);
  }

  console.log('✅ localStorage cleanup complete');
}

const FORCE_RESYNC_MARKER = (userId: string) => `forceResync_applied_${userId}`;

/**
 * Honor an admin force-resync token. When an admin edits a user's data, the
 * user's profile `forceResyncToken` is bumped. Here we compare it to a local
 * marker and, when it's newer, authoritatively OVERWRITE the caches that use a
 * max-merge (daily activity, points) with the current Firestore values — so an
 * admin's edit sticks even when they LOWERED a value (the normal merge would
 * otherwise keep the stale higher local value).
 *
 * We overwrite (rather than just delete) so there's never an empty-cache window
 * in which a concurrent level completion — which reads cache-only — could write
 * a near-zero total back to Firestore. The reconcile marker is safe to simply
 * drop (it only forces the next freeze reconciliation to re-run).
 * Returns true if a resync was applied.
 */
async function consumeForceResyncToken(userId: string): Promise<boolean> {
  try {
    const profile = await firestoreService.getUserProfile(userId);
    const token = profile?.forceResyncToken;
    if (!token) return false;

    if (localStorage.getItem(FORCE_RESYNC_MARKER(userId)) === token) return false;

    // Pull authoritative values, then overwrite the local caches atomically.
    const [activities, points] = await Promise.all([
      firestoreService.getAllDailyActivities(userId),
      firestoreService.getUserPoints(userId),
    ]);

    try {
      localStorage.setItem(`daily_activity_${userId}`, JSON.stringify(activities));
      localStorage.setItem(
        `user_points_${userId}`,
        JSON.stringify(points ?? { total: 0, earned: 0, spent: 0, history: [] }),
      );
    } catch (cacheError) {
      // Quota or serialization issue — fall back to clearing so the downstream
      // sync-down repopulates. (Worst case: brief empty-cache window.)
      console.warn('Force-resync cache overwrite failed, clearing instead:', cacheError);
      localStorage.removeItem(`daily_activity_${userId}`);
      localStorage.removeItem(`user_points_${userId}`);
    }

    // Admin edits (deleted freeze events, repaired days, changed windows) only
    // take effect once the reconciler runs again, so the daily marker has to go
    // — for whichever version wrote it.
    clearReconcileMarkers(userId);
    // The in-memory points state outranks the cache by design; drop it too so
    // the admin's authoritative value isn't out-voted by this tab's session.
    resetPointsSessionState(userId);
    localStorage.setItem(FORCE_RESYNC_MARKER(userId), token);

    // Let live UI (header points pill, streak ring) refresh immediately.
    window.dispatchEvent(new CustomEvent('pointsUpdated', {
      detail: { total: (points ?? { total: 0 }).total, synced: true },
    }));
    window.dispatchEvent(new CustomEvent('dailyActivitiesSynced', {
      detail: { syncedCount: activities.length },
    }));

    console.log(`🔁 Applied admin force-resync (${token}) for ${userId} — caches refreshed from Firestore`);
    return true;
  } catch (error) {
    console.warn('Force-resync check failed (continuing):', error);
    return false;
  }
}

/**
 * Sync keyboards from Firestore to localStorage
 */
async function syncKeyboardsFromFirestore(userId: string): Promise<number> {
  try {
    console.log('  🔄 Syncing keyboards from Firestore to localStorage...');
    const keyboards = await firestoreService.getUserKeyboards(userId);
    
    if (keyboards.length === 0) {
      console.log('  ℹ️ No keyboards found in Firestore');
      return 0;
    }

    // Save each keyboard to localStorage
    for (const keyboard of keyboards) {
      const storageKey = `keyboard_${userId}_${keyboard.id}`;
      localStorage.setItem(storageKey, JSON.stringify(keyboard));
      console.log(`  💾 Synced keyboard to localStorage: ${keyboard.name} (${keyboard.id})`);
    }

    // If there's a default keyboard in the keyboards list, but no default set in localStorage
    const defaultKeyKey = `user_default_keyboard_${userId}`;
    const currentDefault = localStorage.getItem(defaultKeyKey);
    
    if (!currentDefault && keyboards.length > 0) {
      // Set the first keyboard as default
      localStorage.setItem(defaultKeyKey, keyboards[0].id);
      console.log(`  ⌨️ Set default keyboard: ${keyboards[0].name}`);
    }

    return keyboards.length;
  } catch (error) {
    console.error('  ❌ Error syncing keyboards from Firestore:', error);
    return 0;
  }
}

/**
 * Sync user data with Firestore
 * Bi-directional sync:
 * - If Firestore has data but localStorage doesn't → Sync down
 * - If localStorage has data but Firestore doesn't → Clear localStorage (admin deleted)
 * - If both have data → Keep both (localStorage is cache)
 */
export async function syncUserDataWithFirestore(userId: string): Promise<{
  syncPerformed: boolean;
  clearedProgress: boolean;
  clearedKeyboards: boolean;
  clearedProfile: boolean;
  syncedKeyboards: number;
  syncedAggregations: number;
  syncedPoints: boolean;
  syncedDailyActivities: number;
}> {
  try {
    console.log('🔄 Syncing user data with Firestore for:', userId);

    // Honor any pending admin force-resync BEFORE inspecting localStorage, so
    // the status checks below see the cleared (cold) caches and re-pull
    // authoritatively from Firestore.
    await consumeForceResyncToken(userId);

    // Check what exists in Firestore
    const firestoreStatus = await firestoreService.checkUserDataExists(userId);
    console.log('  📊 Firestore status:', firestoreStatus);

    // If offline, skip sync entirely to avoid clearing localStorage
    if (firestoreStatus.isOffline) {
      console.log('  🔌 Client is offline. Skipping sync to preserve localStorage data.');
      return {
        syncPerformed: false,
        clearedProgress: false,
        clearedKeyboards: false,
        clearedProfile: false,
        syncedKeyboards: 0,
        syncedAggregations: 0,
        syncedPoints: false,
        syncedDailyActivities: 0,
      };
    }

    // Check what exists in localStorage
    const localStorageStatus = {
      hasProgress: false,
      hasKeyboards: false,
      hasProfile: false,
      hasAggregations: false,
      hasPoints: false,
    };

    // Check for progress data in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (key.startsWith(`level_completion_${userId}_`) || 
            key.startsWith(`basicsProgress_${userId}`) ||
            key.startsWith(`pythonProgress_${userId}`)) {
          localStorageStatus.hasProgress = true;
        }
        if (key.startsWith(`agg_${userId}_`)) {
          localStorageStatus.hasAggregations = true;
        }
        if (key.startsWith(`keyboard_${userId}_`)) {
          localStorageStatus.hasKeyboards = true;
        }
        if (key === `userProfile_${userId}`) {
          localStorageStatus.hasProfile = true;
        }
        if (key === `user_points_${userId}`) {
          localStorageStatus.hasPoints = true;
        }
      }
    }

    console.log('  💾 localStorage status:', localStorageStatus);

    let syncedKeyboards = 0;
    let syncedAggregations = 0;
    let syncedPoints = false;

    // Determine what needs to be cleared OR synced down
    const shouldClearProgress = localStorageStatus.hasProgress && !firestoreStatus.hasTrainingSessions;
    const shouldClearKeyboards = localStorageStatus.hasKeyboards && !firestoreStatus.hasKeyboards;
    const shouldClearProfile = localStorageStatus.hasProfile && !firestoreStatus.profileExists;
    
    // Sync keyboards DOWN from Firestore if they're missing in localStorage
    const shouldSyncKeyboards = !localStorageStatus.hasKeyboards && firestoreStatus.hasKeyboards;
    
    // Always sync session/combined aggregations (they track course progress!)
    // Only skip if we're sure Firestore has no aggregations at all
    // The syncFromFirestore has quota handling to stop if storage is full
    const shouldSyncAggregations = firestoreStatus.hasTrainingSessions;
    
    // Always try to sync points from Firestore (merge with local, prefer higher total)
    const shouldSyncPoints = true;

    // Clear mismatched data (admin deleted from Firestore)
    if (shouldClearProgress || shouldClearKeyboards || shouldClearProfile) {
      console.log('  ⚠️ Mismatch detected! Data deleted from Firestore. Clearing localStorage...');
      clearUserDataFromLocalStorage(userId, {
        clearProgress: shouldClearProgress,
        clearKeyboards: shouldClearKeyboards,
        clearProfile: shouldClearProfile,
      });
    }

    // Sync keyboards down from Firestore
    if (shouldSyncKeyboards) {
      console.log('  ⬇️ Keyboards exist in Firestore but not localStorage. Syncing down...');
      syncedKeyboards = await syncKeyboardsFromFirestore(userId);
    }

    // Sync aggregations (progress data) down from Firestore
    if (shouldSyncAggregations) {
      console.log('  ⬇️ Syncing course progress (session/combined aggregations) from Firestore...');
      const result = await aggregationService.syncFromFirestore(userId);
      syncedAggregations = result.synced;
    }

    // Sync per-language word stats from Firestore. Cheap (3 docs max) and
    // gracefully no-ops when nothing's there, so unconditional.
    try {
      const { wordStatsService } = await import('../services/WordStatsService');
      const wsLoaded = await wordStatsService.loadFromFirestore(userId);
      if (wsLoaded > 0) console.log(`  ⬇️ Loaded ${wsLoaded} word-stats doc(s) from Firestore`);
    } catch (err) {
      console.warn('  ⚠️ Word stats hydration skipped:', err);
    }
    
    // Sync points down from Firestore
    if (shouldSyncPoints) {
      console.log('  ⬇️ No local points found. Syncing points from Firestore...');
      syncedPoints = await syncPointsFromFirestore(userId);
    }
    
    // Always sync daily activities from Firestore (merge with local, prefer more data)
    // This ensures today's activity from other browsers is synced
    let syncedDailyActivities = 0;
    console.log('  ⬇️ Syncing daily activities from Firestore...');
    syncedDailyActivities = await syncDailyActivitiesFromFirestore(userId);

    const syncPerformed = shouldClearProgress || shouldClearKeyboards || shouldClearProfile || shouldSyncKeyboards || shouldSyncAggregations || shouldSyncPoints || syncedDailyActivities > 0;

    if (syncPerformed) {
      return {
        syncPerformed: true,
        clearedProgress: shouldClearProgress,
        clearedKeyboards: shouldClearKeyboards,
        clearedProfile: shouldClearProfile,
        syncedKeyboards,
        syncedAggregations,
        syncedPoints,
        syncedDailyActivities,
      };
    }

    console.log('  ✅ Data in sync, no changes needed');
    return {
      syncPerformed: false,
      clearedProgress: false,
      clearedKeyboards: false,
      clearedProfile: false,
      syncedKeyboards: 0,
      syncedAggregations: 0,
      syncedPoints: false,
      syncedDailyActivities: 0,
    };
  } catch (error) {
    console.error('❌ Error syncing user data:', error);
    return {
      syncPerformed: false,
      clearedProgress: false,
      clearedKeyboards: false,
      clearedProfile: false,
      syncedKeyboards: 0,
      syncedAggregations: 0,
      syncedPoints: false,
      syncedDailyActivities: 0,
    };
  }
}

/**
 * Schedule periodic sync checks
 * Call this when the user logs in or when the app initializes
 */
export function scheduleDataSync(userId: string): void {
  // Immediate sync on login
  syncUserDataWithFirestore(userId);

  // Periodic sync every 5 minutes
  const syncInterval = setInterval(() => {
    syncUserDataWithFirestore(userId);
  }, 5 * 60 * 1000); // 5 minutes

  // Store interval ID for cleanup
  (window as any).__dataSyncInterval = syncInterval;
}

/**
 * Stop scheduled sync
 * Call this when the user logs out
 */
export function stopDataSync(): void {
  const syncInterval = (window as any).__dataSyncInterval;
  if (syncInterval) {
    clearInterval(syncInterval);
    delete (window as any).__dataSyncInterval;
  }
}

